import type { Session } from "@supabase/supabase-js";
import {
  clearStaleAuthSession,
  isInvalidRefreshTokenMessage,
  supabase,
} from "@/lib/supabaseClient";

/**
 * 직접 관리하는 세션 갱신 관리자.
 *
 * 라이브러리의 autoRefreshToken 을 끈 대신, 만료 60초 전에 미리
 * refreshSession() 을 호출한다. 모든 호출은 await + try/catch 로 감싸서
 * "Invalid Refresh Token" 같은 거부가 처리되지 않은 채로 새어 나가지 않도록 한다.
 */

const REFRESH_LEAD_MS = 60_000;
const MIN_DELAY_MS = 10_000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshing = false;
let hasSession = false;
let listenersBound = false;

function clearTimer(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleAt(expiresAtSeconds?: number | null): void {
  clearTimer();
  if (!expiresAtSeconds) return;
  const expiresAtMs = expiresAtSeconds * 1000;
  const delay = Math.max(expiresAtMs - Date.now() - REFRESH_LEAD_MS, MIN_DELAY_MS);
  refreshTimer = setTimeout(() => {
    void refreshSessionNow();
  }, delay);
}

/** 세션을 즉시 갱신한다. 실패해도 절대 밖으로 던지지 않는다. */
export async function refreshSessionNow(): Promise<void> {
  if (!hasSession || refreshing) return;
  refreshing = true;
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      if (isInvalidRefreshTokenMessage(error.message)) {
        await clearStaleAuthSession();
      }
      return;
    }
    scheduleAt(data.session?.expires_at);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (isInvalidRefreshTokenMessage(message)) {
      await clearStaleAuthSession();
    }
  } finally {
    refreshing = false;
  }
}

/** 현재 세션에 맞춰 갱신 예약을 다시 잡는다. */
export function syncRefreshSchedule(session: Session | null): void {
  hasSession = Boolean(session);
  if (!session) {
    clearTimer();
    return;
  }
  scheduleAt(session.expires_at);
}

/** 창 포커스/탭 복귀 시 세션이 살아있도록 갱신한다. (한 번만 등록) */
export function bindAuthRefreshListeners(): void {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;

  window.addEventListener("focus", () => {
    void refreshSessionNow();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void refreshSessionNow();
    }
  });
}